import React, { useEffect, useState } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../../components/ui/select';
import {
  Activity, Search, RefreshCw, ChevronLeft, ChevronRight, Clock
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';

const entityTypes = ['tenant', 'user', 'tenant_settings', 'lead', 'application', 'document'];

const actionColors = {
  created: 'bg-green-100 text-green-700',
  updated: 'bg-blue-100 text-blue-700',
  suspended: 'bg-red-100 text-red-700',
  deactivated: 'bg-gray-100 text-gray-600',
  activated: 'bg-green-100 text-green-700',
  archived: 'bg-gray-100 text-gray-600',
  invited: 'bg-indigo-100 text-indigo-700',
  password_reset: 'bg-amber-100 text-amber-700',
  invite_resent: 'bg-blue-100 text-blue-700',
};

const getActionColor = (action) => {
  if (!action) return 'bg-gray-100 text-gray-600';
  for (const [key, value] of Object.entries(actionColors)) {
    if (action.includes(key)) return value;
  }
  return 'bg-gray-100 text-gray-600';
};

export default function AuditLogs() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [filters, setFilters] = useState({ tenant_id: '', action: '', entity_type: '' });
  const pageSize = 25;

  useEffect(() => { loadTenants(); }, []);
  useEffect(() => { loadLogs(); }, [page, filters]);

  const loadTenants = async () => {
    try {
      const data = await api.getTenants();
      setTenants(data);
    } catch (err) {
      console.error('[AuditLogs] Failed to load tenants:', err);
    }
  };

  const loadLogs = async () => {
    setLoading(true);
    try {
      const data = await api.getAuditLogs({
        ...filters,
        limit: pageSize,
        skip: page * pageSize,
      });
      setLogs(data.logs || []);
      setTotal(data.total || 0);
    } catch (err) {
      toast.error('Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  };

  const setFilter = (k, v) => {
    setPage(0);
    setFilters((f) => ({ ...f, [k]: v === 'all' ? '' : v }));
  };

  const totalPages = Math.ceil(total / pageSize);

  const formatTime = (ts) => {
    if (!ts) return '';
    const d = new Date(ts);
    return d.toLocaleString();
  };

  const getTenantName = (tenantId) => {
    if (!tenantId) return 'Platform';
    const t = tenants.find((t) => t.id === tenantId);
    return t?.company_name || tenantId.slice(-8);
  };

  return (
    <div className="space-y-6" data-testid="audit-logs-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Audit Logs</h1>
          <p className="text-sm text-muted-foreground mt-1">{total} entries</p>
        </div>
        <Button variant="outline" size="icon" onClick={loadLogs} data-testid="refresh-audit-logs-btn">
          <RefreshCw className="h-4 w-4" />
        </Button>
      </div>

      {/* Filters */}
      <Card className="p-4">
        <div className="flex flex-wrap gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by action..."
              value={filters.action}
              onChange={(e) => setFilter('action', e.target.value)}
              className="pl-9"
              data-testid="search-audit-input"
            />
          </div>
          <Select value={filters.tenant_id || 'all'} onValueChange={(v) => setFilter('tenant_id', v)}>
            <SelectTrigger className="w-[200px]" data-testid="filter-audit-tenant">
              <SelectValue placeholder="All Tenants" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Tenants</SelectItem>
              {tenants.map((t) => (
                <SelectItem key={t.id} value={t.id}>{t.company_name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filters.entity_type || 'all'} onValueChange={(v) => setFilter('entity_type', v)}>
            <SelectTrigger className="w-[170px]" data-testid="filter-audit-entity">
              <SelectValue placeholder="All Entities" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Entities</SelectItem>
              {entityTypes.map((e) => (
                <SelectItem key={e} value={e}>{e.replace(/_/g, ' ')}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </Card>

      {/* Table */}
      <Card>
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="audit-logs-table">
              <thead>
                <tr className="border-b text-left text-sm text-muted-foreground">
                  <th className="px-6 py-3 font-medium">Timestamp</th>
                  <th className="px-6 py-3 font-medium">Action</th>
                  <th className="px-6 py-3 font-medium">Entity</th>
                  <th className="px-6 py-3 font-medium">Tenant</th>
                  <th className="px-6 py-3 font-medium">Actor Role</th>
                  <th className="px-6 py-3 font-medium">Details</th>
                </tr>
              </thead>
              <tbody>
                {logs.length === 0 && (
                  <tr><td colSpan={6} className="text-center py-12 text-muted-foreground">No audit logs found</td></tr>
                )}
                {logs.map((log, idx) => (
                  <tr key={log.id || idx} className="border-b last:border-0 hover:bg-accent/50 transition-colors" data-testid={`audit-row-${idx}`}>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Clock className="h-3.5 w-3.5" />
                        <span>{formatTime(log.created_at)}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getActionColor(log.action)}`}>
                        {log.action?.replace(/\./g, ' ')}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div>
                        <p className="text-sm font-medium capitalize">{log.entity_type?.replace(/_/g, ' ')}</p>
                        {log.entity_id && <p className="text-xs text-muted-foreground font-mono">...{log.entity_id.slice(-8)}</p>}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm">{getTenantName(log.tenant_id)}</td>
                    <td className="px-6 py-4">
                      <span className="text-xs font-medium px-2 py-1 bg-accent rounded capitalize">
                        {log.user_role?.replace(/_/g, ' ') || '-'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      {log.new_value_json && (
                        <span className="text-xs text-muted-foreground font-mono max-w-[200px] truncate block">
                          {typeof log.new_value_json === 'string' ? log.new_value_json : JSON.stringify(log.new_value_json).slice(0, 60)}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-6 py-3 border-t">
            <p className="text-sm text-muted-foreground">
              Page {page + 1} of {totalPages} ({total} total)
            </p>
            <div className="flex gap-1">
              <Button variant="outline" size="icon" onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0} data-testid="audit-prev-page">
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button variant="outline" size="icon" onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1} data-testid="audit-next-page">
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
